import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Search, Filter, ArrowUpDown, X, Edit2, Plus, Power, ArrowLeft, Save, Upload, Trash2, Image, Sliders } from 'lucide-react';
import AppContext from '../../context/AppContext';
import '../../assests/css/admin/Category.css';
import CustomizationOptionsModal from './CustomizationOptionsModal';

// ─────────────────────────────────────────────
// SHARED API HELPER
// ─────────────────────────────────────────────
const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('adminToken');
  const headers = {
    ...(options.headers || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(url, { ...options, headers });
  const raw = await response.text();

  const jsonStart = raw.search(/[{[]/);
  if (jsonStart === -1) {
    console.error(`No JSON found in response from ${url}:`, raw.slice(0, 300));
    throw new Error(`Server returned an unexpected response (status ${response.status}).`);
  }

  let data;
  try {
    data = JSON.parse(raw.slice(jsonStart));
  } catch (e) {
    console.error(`JSON parse error from ${url}:`, raw.slice(0, 300));
    throw new Error('Invalid JSON response from server.');
  }

  if (!response.ok) {
    throw new Error(data.message || `Request failed: ${response.status}`);
  }

  return data;
};

// ─────────────────────────────────────────────
// SHARED IMAGE UPLOAD FIELD
// ─────────────────────────────────────────────
const ImageUploadField = ({ preview, onSelect, onRemove, fileInputRef, error }) => (
  <div className="AdminCat-section">
    <div className="AdminCat-section-header">
      <span className="AdminCat-section-title">📷 Category Image</span>
      <span className="AdminCat-badge-required">Required</span>
    </div>
    {!preview ? (
      <div
        className="AdminCat-upload-box"
        onClick={() => fileInputRef.current?.click()}
        style={{ border: error ? '2px solid #ef4444' : undefined }}
      >
        <Upload size={40} color={error ? '#ef4444' : '#8b5cf6'} />
        <p className="AdminCat-upload-text">Click to upload image</p>
        <p className="AdminCat-upload-hint">PNG, JPG, any size supported</p>
      </div>
    ) : (
      <div className="AdminCat-img-preview-wrap">
        <div className="AdminCat-img-preview">
          <img
            src={preview}
            alt="preview"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div className="AdminCat-img-actions">
          <button type="button" className="AdminCat-change-btn" onClick={() => fileInputRef.current?.click()}>
            <Upload size={14} /> Change
          </button>
          <button type="button" className="AdminCat-remove-btn" onClick={onRemove}>
            <Trash2 size={14} /> Remove
          </button>
        </div>
      </div>
    )}
    {error && <p style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.35rem' }}>{error}</p>}
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      onChange={onSelect}
      style={{ display: 'none' }}
    />
  </div>
);

// ─────────────────────────────────────────────
// SHARED STATUS TOGGLE
// ─────────────────────────────────────────────
const StatusToggle = ({ status, onChange }) => (
  <div className="AdminCat-form-group">
    <label className="AdminCat-label">Status</label>
    <div className="AdminCat-status-toggle-row">
      <span className={`AdminCat-status-label ${status === 'active' ? 'active' : ''}`}>
        {status === 'active' ? 'Active' : 'Inactive'}
      </span>
      <div
        className={`AdminCat-toggle-switch ${status === 'active' ? 'AdminCat-toggle-on' : ''}`}
        onClick={() => onChange(status === 'active' ? 'inactive' : 'active')}
        role="switch"
        aria-checked={status === 'active'}
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onChange(status === 'active' ? 'inactive' : 'active')}
      >
        <div className="AdminCat-toggle-knob" />
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// SHARED CATEGORY FORM (used by Add & Edit)
// ─────────────────────────────────────────────
const CategoryForm = ({
  initialName = '',
  initialStatus = 'active',
  initialImageUrl = null,
  submitLabel = 'Save Category',
  onSubmit,
  onBack,
  showSweetAlert,
  title,
  subtitle,
}) => {
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [categoryName, setCategoryName] = useState(initialName);
  const [status, setStatus] = useState(initialStatus);
  const [existingImageUrl, setExistingImageUrl] = useState(initialImageUrl);
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [errors, setErrors] = useState({ categoryName: '', image: '' });

  const displayPreview = newImagePreview || existingImageUrl;

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // ✅ Sirf image type check - koi size restriction nahi
    if (!file.type.startsWith('image/')) {
      showSweetAlert('Please select a valid image file!');
      return;
    }
    setNewImage(file);
    setErrors((prev) => ({ ...prev, image: '' }));
    const reader = new FileReader();
    reader.onloadend = () => setNewImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setNewImage(null);
    setNewImagePreview(null);
    setExistingImageUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validate = () => {
    const newErrors = { categoryName: '', image: '' };
    let valid = true;
    if (!categoryName.trim()) {
      newErrors.categoryName = 'Category name is required';
      valid = false;
    }
    if (!existingImageUrl && !newImage) {
      newErrors.image = 'Please upload a category image';
      valid = false;
    }
    setErrors(newErrors);
    return valid;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({ categoryName: categoryName.trim(), status, newImage, existingImageUrl });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="AdminCategoryAdminSubCategory-admin-category-container">
      <div className="AdminCategoryAdminSubCategory-category-header">
        <button className="AdminCat-back-btn" onClick={onBack}>
          <ArrowLeft size={18} /> Back to Categories
        </button>
        <h1 className="AdminCategoryAdminSubCategory-category-title">{title}</h1>
        <p className="AdminCategoryAdminSubCategory-category-subtitle">{subtitle}</p>
      </div>

      <div className="AdminCat-simple-form-layout">
        <ImageUploadField
          preview={displayPreview}
          onSelect={handleImageSelect}
          onRemove={handleRemoveImage}
          fileInputRef={fileInputRef}
          error={errors.image}
        />

        <div className="AdminCat-section">
          <div className="AdminCat-section-header">
            <span className="AdminCat-section-title">📝 Basic Information</span>
            <span className="AdminCat-badge-required">Required</span>
          </div>

          <div className="AdminCat-form-group">
            <label className="AdminCat-label">
              Category Name <span className="AdminCat-star">*</span>
            </label>
            <input
              className="AdminCat-input"
              type="text"
              value={categoryName}
              onChange={(e) => {
                setCategoryName(e.target.value);
                if (e.target.value.trim()) setErrors((prev) => ({ ...prev, categoryName: '' }));
              }}
              placeholder="e.g., Women's Fashion, Electronics"
              style={{ borderColor: errors.categoryName ? '#ef4444' : undefined }}
            />
            {errors.categoryName && (
              <p style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.35rem' }}>
                {errors.categoryName}
              </p>
            )}
          </div>

          <StatusToggle status={status} onChange={setStatus} />
        </div>

        <div className="AdminCat-actions">
          <button className="AdminCat-cancel-btn" onClick={onBack} disabled={submitting}>
            Cancel
          </button>
          <button className="AdminCat-save-btn" onClick={handleSave} disabled={submitting}>
            <Save size={16} /> {submitting ? 'Saving...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// ADD CATEGORY VIEW
// ─────────────────────────────────────────────
const AddCategoryView = ({ baseUrl, onBack, onSaved, showSweetAlert }) => {
  const handleSubmit = async ({ categoryName, status, newImage }) => {
    const formData = new FormData();
    formData.append('category_name', categoryName);
    formData.append('status', status);
    formData.append('image', newImage);

    const data = await apiFetch(`${baseUrl}/admin/category/createCategory`, {
      method: 'POST',
      body: formData,
    });

    if (data.success) {
      showSweetAlert('Category created successfully!');
      setTimeout(() => onSaved(), 1000);
    } else {
      showSweetAlert(data.message || 'Failed to create category');
    }
  };

  return (
    <CategoryForm
      title="Add New Category"
      subtitle="Create a new product category with an image"
      submitLabel="Save Category"
      onSubmit={handleSubmit}
      onBack={onBack}
      showSweetAlert={showSweetAlert}
    />
  );
};

// ─────────────────────────────────────────────
// EDIT CATEGORY VIEW
// ─────────────────────────────────────────────
const EditCategoryView = ({ baseUrl, category, onBack, onSaved, showSweetAlert }) => {
  const handleSubmit = async ({ categoryName, status, newImage }) => {
    const formData = new FormData();
    formData.append('category_name', categoryName);
    formData.append('status', status);
    formData.append('id', category.id);
    if (newImage) formData.append('image', newImage);

    const data = await apiFetch(`${baseUrl}/admin/category/updateCategory/${category.id}`, {
      method: 'POST',
      body: formData,
    });

    if (data.success) {
      showSweetAlert('Category updated successfully!');
      setTimeout(() => onSaved(), 1000);
    } else {
      showSweetAlert(data.message || 'Failed to update category');
    }
  };

  return (
    <CategoryForm
      title="Edit Category"
      subtitle="Update category name, image and status"
      submitLabel="Update Category"
      initialName={category?.category_name || ''}
      initialStatus={category?.status || 'active'}
      initialImageUrl={category?.image_url || null}
      onSubmit={handleSubmit}
      onBack={onBack}
      showSweetAlert={showSweetAlert}
    />
  );
};

// ─────────────────────────────────────────────
// ADD SUBCATEGORY MODAL
// ─────────────────────────────────────────────
const AddSubCategoryModal = ({ baseUrl, categoryId, onClose, onAdd, showSweetAlert }) => {
  const imgRef = useRef(null);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [status, setStatus] = useState('active');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleImg = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // ✅ Sirf image type check - koi size restriction nahi
    if (!file.type.startsWith('image/')) {
      showSweetAlert('Please select a valid image file!');
      return;
    }
    setImage(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleAdd = async () => {
    if (!name.trim()) { setNameError('Sub-category name is required'); return; }
    setNameError('');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('mlcategory_id', categoryId);
      formData.append('title', name.trim());
      formData.append('status', status);
      if (image) formData.append('image', image);

      const data = await apiFetch(`${baseUrl}/admin/subcategory/createSubcategory`, {
        method: 'POST',
        body: formData,
      });

      if (data.success) {
        onAdd(data.data);
        showSweetAlert('Sub-category added successfully!');
        onClose();
      } else {
        showSweetAlert(data.message || 'Failed to add sub-category');
      }
    } catch (err) {
      showSweetAlert(err.message || 'Failed to add sub-category');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="AdminCat-modal-overlay" onClick={onClose}>
      <div className="AdminCat-modal" onClick={(e) => e.stopPropagation()}>
        <div className="AdminCat-modal-header">
          <h3 className="AdminCat-modal-title">Add Sub-Category</h3>
          <button className="AdminCat-modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="AdminCat-modal-img-area" onClick={() => imgRef.current?.click()}>
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="sub"
              className="AdminCat-modal-img-preview"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <>
              <Image size={28} color="#8b5cf6" />
              <p className="AdminCat-modal-img-hint">Click to upload image (any size)</p>
            </>
          )}
          <input
            ref={imgRef}
            type="file"
            accept="image/*"
            onChange={handleImg}
            style={{ display: 'none' }}
          />
        </div>

        <div className="AdminCat-form-group" style={{ marginTop: '1rem' }}>
          <label className="AdminCat-label">
            Sub-Category Name <span className="AdminCat-star">*</span>
          </label>
          <input
            className="AdminCat-input"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (e.target.value.trim()) setNameError('');
            }}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            placeholder="e.g., Tops & Blouses"
            autoFocus
            style={{ borderColor: nameError ? '#ef4444' : undefined }}
          />
          {nameError && (
            <p style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: '0.35rem' }}>{nameError}</p>
          )}
        </div>

        <StatusToggle status={status} onChange={setStatus} />

        <div className="AdminCat-modal-actions">
          <button className="AdminCat-cancel-btn" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="AdminCat-save-btn" onClick={handleAdd} disabled={loading}>
            <Plus size={15} /> {loading ? 'Adding...' : 'Add Sub-Category'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const AdminCategory = () => {
  const { baseUrl } = useContext(AppContext);
  const [view, setView] = useState('list');
  const [editingCategory, setEditingCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('products');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showAddSubModal, setShowAddSubModal] = useState(false);

  // Customization options modal ke liye naye states
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [customizeSubcategory, setCustomizeSubcategory] = useState(null);

  const alertTimerRef = useRef(null);
  const filterRef = useRef(null);
  const sortRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilterMenu(false);
      if (sortRef.current && !sortRef.current.contains(e.target)) setShowSortMenu(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    return () => { if (alertTimerRef.current) clearTimeout(alertTimerRef.current); };
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch(`${baseUrl}/admin/category/getAllCategories`);
      if (data.success && Array.isArray(data.data)) {
        setCategories(data.data);
        setSelectedCategory((prev) =>
          prev ? data.data.find((c) => c.id === prev.id) || null : null
        );
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const showSweetAlert = (message) => {
    setAlertMessage(message);
    setShowAlert(true);
    if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    alertTimerRef.current = setTimeout(() => setShowAlert(false), 3000);
  };

  const handleToggleStatus = async (categoryId, e) => {
    e.stopPropagation();
    const cat = categories.find((c) => c.id === categoryId);
    const newStatus = cat.status === 'active' ? 'inactive' : 'active';
    try {
      const data = await apiFetch(`${baseUrl}/admin/category/toggleStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: categoryId, status: newStatus }),
      });
      if (!data.success) throw new Error(data.message || 'Toggle failed');
      const updated = categories.map((c) => c.id === categoryId ? { ...c, status: newStatus } : c);
      setCategories(updated);
      if (selectedCategory?.id === categoryId) setSelectedCategory(updated.find((c) => c.id === categoryId));
      showSweetAlert(`Category ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully!`);
    } catch (err) {
      showSweetAlert(err.message || 'Failed to toggle category status');
    }
  };

  const handleToggleSubStatus = async (subId) => {
    const sub = (selectedCategory.sub_categories || []).find((s) => s.id === subId);
    const newStatus = sub.status === 'active' ? 'inactive' : 'active';
    try {
      const data = await apiFetch(`${baseUrl}/admin/subcategory/toggleStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: subId, status: newStatus }),
      });
      if (!data.success) throw new Error(data.message || 'Toggle failed');
      const updatedSubs = (selectedCategory.sub_categories || []).map((s) =>
        s.id === subId ? { ...s, status: newStatus } : s
      );
      const updated = categories.map((c) =>
        c.id === selectedCategory.id ? { ...c, sub_categories: updatedSubs } : c
      );
      setCategories(updated);
      setSelectedCategory({ ...selectedCategory, sub_categories: updatedSubs });
    } catch (err) {
      showSweetAlert(err.message || 'Failed to toggle sub-category status');
    }
  };

  const handleDeleteSubCategory = async (subId, subName) => {
    try {
      const data = await apiFetch(`${baseUrl}/admin/subcategory/deleteSubcategory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: subId }),
      });
      if (!data.success) throw new Error(data.message || 'Delete failed');
      const updatedSubs = (selectedCategory.sub_categories || []).filter((s) => s.id !== subId);
      const updated = categories.map((c) =>
        c.id === selectedCategory.id ? { ...c, sub_categories: updatedSubs } : c
      );
      setCategories(updated);
      setSelectedCategory({ ...selectedCategory, sub_categories: updatedSubs });
      showSweetAlert(`"${subName}" deleted successfully!`);
    } catch (err) {
      showSweetAlert(err.message || 'Failed to delete sub-category');
    }
  };

  const handleAddSubCategory = (newSub) => {
    const updatedSubs = [...(selectedCategory.sub_categories || []), newSub];
    const updated = categories.map((c) =>
      c.id === selectedCategory.id ? { ...c, sub_categories: updatedSubs } : c
    );
    setCategories(updated);
    setSelectedCategory({ ...selectedCategory, sub_categories: updatedSubs });
  };

  const filteredCategories = categories
    .filter((cat) => {
      const name = (cat.category_name || '').toLowerCase();
      return (
        name.includes(searchQuery.toLowerCase()) &&
        (filterStatus === 'all' || cat.status === filterStatus)
      );
    })
    .sort((a, b) => {
      if (sortBy === 'name') return (a.category_name || '').localeCompare(b.category_name || '');
      if (sortBy === 'products') return (b.total_products || 0) - (a.total_products || 0);
      return 0;
    });

  if (view === 'add') {
    return (
      <AddCategoryView
        baseUrl={baseUrl}
        onBack={() => setView('list')}
        onSaved={() => { fetchCategories(); setView('list'); }}
        showSweetAlert={showSweetAlert}
      />
    );
  }

  if (view === 'edit' && editingCategory) {
    return (
      <EditCategoryView
        baseUrl={baseUrl}
        category={editingCategory}
        onBack={() => { setView('list'); setEditingCategory(null); }}
        onSaved={() => { fetchCategories(); setView('list'); setEditingCategory(null); }}
        showSweetAlert={showSweetAlert}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem', color: '#6b7280' }}>
        <div style={{ width: 48, height: 48, border: '4px solid #e5e7eb', borderTopColor: '#8b5cf6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <p>Loading categories...</p>
      </div>
    );
  }

  return (
    <div className="AdminCategoryAdminSubCategory-admin-category-container">
      {showAlert && (
        <div className="AdminCategoryAdminSubCategory-sweet-alert">
          <div className="AdminCategoryAdminSubCategory-alert-icon">✓</div>
          <div className="AdminCategoryAdminSubCategory-alert-message">{alertMessage}</div>
        </div>
      )}

      {showAddSubModal && (
        <AddSubCategoryModal
          baseUrl={baseUrl}
          categoryId={selectedCategory?.id}
          onClose={() => setShowAddSubModal(false)}
          onAdd={handleAddSubCategory}
          showSweetAlert={showSweetAlert}
        />
      )}

      {showCustomizeModal && customizeSubcategory && (
        <CustomizationOptionsModal
          baseUrl={baseUrl}
          subcategoryId={customizeSubcategory.id}
          subcategoryName={customizeSubcategory.title}
          onClose={() => { setShowCustomizeModal(false); setCustomizeSubcategory(null); }}
          showSweetAlert={showSweetAlert}
        />
      )}

      <div className="AdminCategoryAdminSubCategory-category-header">
        <h1 className="AdminCategoryAdminSubCategory-category-title">Category Overview</h1>
        <p className="AdminCategoryAdminSubCategory-category-subtitle">
          Manage product hierarchy, organization, and visibility.
        </p>
      </div>

      <div className="AdminCategoryAdminSubCategory-category-controls">
        <div className="AdminCategoryAdminSubCategory-search-filter-wrapper">
          <div className="AdminCategoryAdminSubCategory-search-box">
            <Search size={18} className="AdminCategoryAdminSubCategory-search-icon" />
            <input
              type="text"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="AdminCategoryAdminSubCategory-search-input"
            />
          </div>

          <div className="AdminCategoryAdminSubCategory-dropdown-wrapper" ref={filterRef}>
            <button
              className="AdminCategoryAdminSubCategory-control-btn"
              onClick={() => { setShowFilterMenu((v) => !v); setShowSortMenu(false); }}
            >
              <Filter size={16} /> Filter
            </button>
            {showFilterMenu && (
              <div className="AdminCategoryAdminSubCategory-dropdown-menu">
                {[['all', 'All Categories'], ['active', 'Active Only'], ['inactive', 'Inactive Only']].map(([val, label]) => (
                  <div key={val} className="AdminCategoryAdminSubCategory-dropdown-item"
                    onClick={() => { setFilterStatus(val); setShowFilterMenu(false); }}>
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="AdminCategoryAdminSubCategory-dropdown-wrapper" ref={sortRef}>
            <button
              className="AdminCategoryAdminSubCategory-control-btn"
              onClick={() => { setShowSortMenu((v) => !v); setShowFilterMenu(false); }}
            >
              <ArrowUpDown size={16} /> Sort
            </button>
            {showSortMenu && (
              <div className="AdminCategoryAdminSubCategory-dropdown-menu">
                {[['name', 'By Name'], ['products', 'By Products Count']].map(([val, label]) => (
                  <div key={val} className="AdminCategoryAdminSubCategory-dropdown-item"
                    onClick={() => { setSortBy(val); setShowSortMenu(false); }}>
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          className="AdminCategoryAdminSubCategory-control-btn AdminCategoryAdminSubCategory-add-category-btn"
          onClick={() => setView('add')}
        >
          <Plus size={16} /> Add New Category
        </button>
      </div>

      {filteredCategories.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No categories found.</p>
          <p style={{ fontSize: '0.9rem' }}>Try adjusting your search or add a new category.</p>
        </div>
      )}

      <div className="AdminCategoryAdminSubCategory-category-content">
        <div className="AdminCategoryAdminSubCategory-category-grid">
          {filteredCategories.map((category) => (
            <div
              key={category.id}
              className={`AdminCategoryAdminSubCategory-category-card
                ${category.status !== 'active' ? 'AdminCategoryAdminSubCategory-disabled' : ''}
                ${selectedCategory?.id === category.id ? 'AdminCategoryAdminSubCategory-selected' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              <div className="AdminCategoryAdminSubCategory-card-action-buttons">
                <button
                  className="AdminCategoryAdminSubCategory-category-edit-btn"
                  onClick={(e) => { e.stopPropagation(); setEditingCategory(category); setView('edit'); }}
                  title="Edit Category"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  className={`AdminCategoryAdminSubCategory-category-toggle-btn ${
                    category.status === 'active' ? 'AdminCategoryAdminSubCategory-active' : 'AdminCategoryAdminSubCategory-inactive'
                  }`}
                  onClick={(e) => handleToggleStatus(category.id, e)}
                  title={category.status === 'active' ? 'Disable' : 'Enable'}
                >
                  <Power size={14} />
                </button>
              </div>

              <div className="AdminCategoryAdminSubCategory-category-icon" style={{ backgroundColor: category.color || '#E9D5FF' }}>
                <img
                  src={category.image_url}
                  alt={category.category_name}
                  className="AdminCategoryAdminSubCategory-category-img"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span style="font-size:2rem">🖼️</span>';
                  }}
                />
              </div>

              <h3 className="AdminCategoryAdminSubCategory-category-name">{category.category_name}</h3>

              <div className="AdminCategoryAdminSubCategory-category-info">
                <div className="AdminCategoryAdminSubCategory-info-item">
                  <span className="AdminCategoryAdminSubCategory-info-label">Status</span>
                  <span className={`AdminCategoryAdminSubCategory-status-badge AdminCategoryAdminSubCategory-status-${category.status}`}>
                    {category.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="AdminCategoryAdminSubCategory-info-item">
                  <span className="AdminCategoryAdminSubCategory-info-label">Sub-categories</span>
                  <span className="AdminCategoryAdminSubCategory-info-value">{(category.sub_categories || []).length}</span>
                </div>
                <div className="AdminCategoryAdminSubCategory-info-item">
                  <span className="AdminCategoryAdminSubCategory-info-label">Total Products</span>
                  <span className="AdminCategoryAdminSubCategory-info-value">{category.total_products || 0}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedCategory && (
          <div className="AdminCategoryAdminSubCategory-category-details">
            <div className="AdminCategoryAdminSubCategory-details-header">
              <span className="AdminCategoryAdminSubCategory-details-title">CATEGORY DETAILS</span>
              <button className="AdminCategoryAdminSubCategory-close-btn" onClick={() => setSelectedCategory(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="AdminCategoryAdminSubCategory-details-content">
              <div className="AdminCategoryAdminSubCategory-category-profile">
                <div className="AdminCategoryAdminSubCategory-profile-icon" style={{ backgroundColor: selectedCategory.color || '#E9D5FF' }}>
                  <img
                    src={selectedCategory.image_url}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '1rem' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div>
                  <h3 className="AdminCategoryAdminSubCategory-profile-name">{selectedCategory.category_name}</h3>
                  <div className="AdminCategoryAdminSubCategory-profile-badges">
                    <span className="AdminCategoryAdminSubCategory-id-badge">ID: #{selectedCategory.id}</span>
                    <span className={`AdminCategoryAdminSubCategory-status-badge AdminCategoryAdminSubCategory-status-${selectedCategory.status}`}>
                      {selectedCategory.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="AdminCategoryAdminSubCategory-details-section">
                <div className="AdminCategoryAdminSubCategory-section-header">
                  <div className="AdminCategoryAdminSubCategory-section-title-wrapper">
                    <span className="AdminCategoryAdminSubCategory-section-title">
                      Sub-Categories ({(selectedCategory.sub_categories || []).length})
                    </span>
                  </div>
                  <button className="AdminCategoryAdminSubCategory-add-new-btn" onClick={() => setShowAddSubModal(true)}>
                    <Plus size={16} /> Add New
                  </button>
                </div>

                <div className="AdminCat-subcat-cards-list">
                  {(selectedCategory.sub_categories || []).map((sub) => (
                    <div key={sub.id} className={`AdminCat-subcat-card ${sub.status !== 'active' ? 'AdminCat-subcat-card-inactive' : ''}`}>
                      <div className="AdminCat-subcat-card-img">
                        {sub.image_url ? (
                          <img
                            src={sub.image_url}
                            alt={sub.title}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<span style="font-size:1.2rem">🖼️</span>';
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: '1.2rem' }}>📦</span>
                        )}
                      </div>
                      <div className="AdminCat-subcat-card-info">
                        <p className="AdminCat-subcat-card-name">{sub.title}</p>
                        <p className="AdminCat-subcat-card-products">{sub.products_count || 0} products</p>
                      </div>
                      <button
                        className="AdminCat-subcat-card-delete"
                        onClick={() => { setCustomizeSubcategory(sub); setShowCustomizeModal(true); }}
                        title="Manage Customization Options"
                        style={{ color: '#8b5cf6' }}
                      >
                        <Sliders size={14} />
                      </button>
                      <div
                        className={`AdminCat-toggle-switch AdminCat-toggle-sm ${sub.status === 'active' ? 'AdminCat-toggle-on' : ''}`}
                        onClick={() => handleToggleSubStatus(sub.id)}
                        title={sub.status === 'active' ? 'Deactivate' : 'Activate'}
                        role="switch"
                        aria-checked={sub.status === 'active'}
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleToggleSubStatus(sub.id)}
                      >
                        <div className="AdminCat-toggle-knob" />
                      </div>
                      <button
                        className="AdminCat-subcat-card-delete"
                        onClick={() => handleDeleteSubCategory(sub.id, sub.title)}
                        title="Delete"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}

                  {(selectedCategory.sub_categories || []).length === 0 && (
                    <div className="AdminCat-subcat-empty">
                      <p>No sub-categories yet.</p>
                      <button
                        className="AdminCat-save-btn"
                        style={{ marginTop: '0.5rem', flex: 'none', padding: '0.5rem 1.25rem' }}
                        onClick={() => setShowAddSubModal(true)}
                      >
                        <Plus size={14} /> Add First Sub-Category
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="AdminCategoryAdminSubCategory-details-actions">
                <button
                  className="AdminCategoryAdminSubCategory-save-btn"
                  onClick={() => { setEditingCategory(selectedCategory); setView('edit'); }}
                >
                  Edit Category
                </button>
                <button className="AdminCategoryAdminSubCategory-cancel-btn" onClick={() => setSelectedCategory(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCategory;